import { useEffect, useState, useCallback } from 'react'
import { fetchMe, type AppUser, logout as logoutApi } from './auth'
import { getToken } from './api'
import { resetAllUserCaches } from './cacheReset'
import { applyPendingReferralIfAuthenticated } from './referralLink'

let cachedUser: AppUser | null | undefined = undefined
let listeners = new Set<(u: AppUser | null) => void>()

function broadcast(u: AppUser | null) {
  // ── Auth-Wechsel-Detection ──────────────────────────────────────────────
  // Wenn der neue User EINE ANDERE ID hat als der gecachte (oder neu/null),
  // ALLE user-bezogenen Caches plätten — sonst zeigt die UI noch Extensions/
  // Org/etc. vom letzten User. Das war der Auslöser des „User A steuert User
  // B's Stempeluhr"-Bugs: A's Daten waren noch sichtbar, aber Aktionen
  // gingen mit B's Token raus.
  const previousId = (cachedUser && cachedUser !== undefined) ? cachedUser.id : null
  const nextId     = u?.id ?? null
  if (previousId !== nextId) {
    resetAllUserCaches()
  }

  cachedUser = u
  listeners.forEach(fn => fn(u))

  // ── Pending-Referral-Code auto-applien ────────────────────────────────
  // Wenn ein User gerade eingeloggt wurde UND ein Code im localStorage
  // liegt (von ?ref=… im Landing), Backend-Apply abfeuern.
  // Failure ist fail-soft — siehe referralLink.ts Logik (4xx löscht,
  // 5xx retried beim nächsten Mount).
  if (u && previousId !== nextId) {
    void applyPendingReferralIfAuthenticated().then(r => {
      if (r.status === 'applied') {
        console.log('[referral] applied:', r.message)
      } else if (r.status === 'rejected') {
        console.log('[referral] rejected:', r.error)
      }
    })
  }
}

/// Globaler Auth-State — beim ersten Mount wird /auth/me aufgerufen wenn ein
/// Token im localStorage liegt. Subsequent-Mounts nutzen den gecachten User.
export function useAuth() {
  const [user, setUser]       = useState<AppUser | null>(cachedUser ?? null)
  const [loading, setLoading] = useState(cachedUser === undefined)

  useEffect(() => {
    const fn = (u: AppUser | null) => setUser(u)
    listeners.add(fn)
    if (cachedUser === undefined) {
      const token = getToken()
      if (!token) {
        broadcast(null); setLoading(false); return
      }
      fetchMe()
        .then(u => { broadcast(u); setLoading(false) })
        .catch(() => { logoutApi(); broadcast(null); setLoading(false) })
    }
    return () => { listeners.delete(fn) }
  }, [])

  const setAuthUser = useCallback((u: AppUser | null) => broadcast(u), [])
  const logout = useCallback(() => { logoutApi(); broadcast(null) }, [])

  return { user, loading, setAuthUser, logout }
}
